import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { visitsAPI, stationsAPI, seedAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import { toast } from "sonner";
import {
  LogIn,
  LogOut,
  Activity,
  Building2,
  Clock,
  ChevronRight,
  AlertTriangle,
  MapPin,
  Calendar,
  User,
} from "lucide-react";
import { formatDateTime, getTimeSince, PURPOSE_LABELS } from "../../../shared/lib/utils";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isSupervisor } = useAuth();
  const [activeVisit, setActiveVisit] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Seed data first
      try {
        await seedAPI.seed();
      } catch {
        // Already seeded, ignore
      }

      const [visitRes, historyRes] = await Promise.all([
        visitsAPI.getActive(),
        visitsAPI.getHistory({ limit: 10 }), // Get last 10 visits
      ]);

      setActiveVisit(visitRes.data);
      setRecentVisits(historyRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="dashboard-page">
      {/* Active Visit Alert */}
      {activeVisit && (
        <Card
          className="border-2 border-emerald-500 bg-emerald-500/5 rounded-none animate-pulse-border cursor-pointer"
          onClick={() => navigate(`/visit/${activeVisit.id}`)}
          data-testid="active-visit-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-emerald-500/20 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-emerald-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold">
                      Currently In Station
                    </span>
                    <Badge className="rounded-none bg-emerald-500 text-white font-mono text-xs">
                      ACTIVE
                    </Badge>
                  </div>
                  <h3 className="font-heading font-bold text-lg">
                    {activeVisit.station_name}
                  </h3>
                  <p className="font-mono text-xs text-muted-foreground">
                    Entry: {formatDateTime(activeVisit.entry_time)} •{" "}
                    {getTimeSince(activeVisit.entry_time)}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!activeVisit ? (
          <Button
            onClick={() => navigate("/entry")}
            className="h-20 rounded-none bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold text-lg flex items-center justify-center gap-3"
            data-testid="station-entry-btn"
          >
            <LogIn className="h-6 w-6" />
            Station Entry
          </Button>
        ) : (
          <Button
            onClick={() => navigate(`/visit/${activeVisit.id}`)}
            className="h-20 rounded-none bg-emerald-600 hover:bg-emerald-700 font-mono uppercase tracking-wider font-bold text-lg flex items-center justify-center gap-3"
            data-testid="view-visit-btn"
          >
            <Activity className="h-6 w-6" />
            Log Activity
          </Button>
        )}

        {activeVisit && (
          <Button
            onClick={() => navigate(`/exit/${activeVisit.id}`)}
            variant="outline"
            className="h-20 rounded-none border-2 font-mono uppercase tracking-wider font-bold text-lg flex items-center justify-center gap-3"
            data-testid="station-exit-btn"
          >
            <LogOut className="h-6 w-6" />
            Station Exit
          </Button>
        )}

        {isSupervisor() && (
          <Button
            onClick={() => navigate("/supervisor")}
            variant="outline"
            className="h-20 rounded-none border-2 font-mono uppercase tracking-wider font-bold text-lg flex items-center justify-center gap-3"
            data-testid="supervisor-dashboard-btn"
          >
            <AlertTriangle className="h-6 w-6" />
            Supervisor Dashboard
          </Button>
        )}

        {isSupervisor() && (
          <Button
            onClick={() => navigate("/map")}
            variant="outline"
            className="h-20 rounded-none border-2 font-mono uppercase tracking-wider font-bold text-lg flex items-center justify-center gap-3 bg-blue-500/10 border-blue-500/50 hover:bg-blue-500/20"
            data-testid="map-view-btn"
          >
            <MapPin className="h-6 w-6 text-blue-500" />
            Station Map
          </Button>
        )}
      </div>

      {/* Recent Visits List */}
      <div>
        <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-[#FF6B00]" />
          Recent Station Visits
        </h2>
        
        {recentVisits.length === 0 ? (
          <Card className="border-2 border-dashed border-border rounded-none">
            <CardContent className="p-8 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg mb-2">No Previous Visits</h3>
              <p className="text-muted-foreground mb-4">
                You haven't visited any stations yet. Start by entering a station.
              </p>
              <Button
                onClick={() => navigate("/entry")}
                className="rounded-none bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
              >
                <LogIn className="h-4 w-4 mr-2" />
                New Station Entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {recentVisits.map((visit) => (
              <Card
                key={visit.id}
                className="border-2 border-border rounded-none industrial-card cursor-pointer hover:border-[#FF6B00]/50 transition-colors"
                onClick={() => navigate(`/history?visit=${visit.id}`)}
                data-testid={`visit-card-${visit.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-muted flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-[#FF6B00]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-base">
                          {visit.station_name}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateTime(visit.entry_time)}
                          </span>
                          {visit.purpose && (
                            <Badge variant="outline" className="rounded-none font-mono text-xs">
                              {PURPOSE_LABELS[visit.purpose] || visit.purpose}
                            </Badge>
                          )}
                        </div>
                        {visit.exit_time && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Duration: {getTimeSince(visit.entry_time, visit.exit_time)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`rounded-none font-mono text-xs ${
                          visit.exit_time
                            ? "bg-zinc-500 text-white"
                            : "bg-emerald-500 text-white"
                        }`}
                      >
                        {visit.exit_time ? "COMPLETED" : "IN PROGRESS"}
                      </Badge>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* View Full History Button */}
            <Button
              variant="outline"
              onClick={() => navigate("/history")}
              className="w-full h-12 rounded-none border-2 font-mono uppercase tracking-wider mt-4"
              data-testid="view-history-btn"
            >
              View Full History
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
